/**
 * Downward Impersonation (support tooling)
 *
 * An admin, client or organization-manager browses the panel as any user
 * with a strictly lower role. The swap happens server-side per request (the
 * API validates the REAL user's role and replaces the principal — see
 * `qr-phone`'s `middlewares/impersonation.js`), so this module only keeps
 * which subject to act as and sends it as a header; it never mints or
 * stores another user's token. Session-scoped on purpose: impersonation is
 * a per-tab support action and must not leak into other tabs or survive the
 * browser closing.
 */

import { ROLE_NAMES_BY_ID } from '@/config/constants';

export const IMPERSONATION_HEADER = 'X-Impersonate-Subject';

const STORAGE_KEY = 'qrphone_admin_impersonation';

/**
 * Strict hierarchy of the panel roles (mirrors the backend's rank map in
 * `middlewares/impersonation.js`). Only roles with a "manage other users"
 * screen in this panel (admin, client, organization-manager) may act as an
 * impersonator; `organization` and `staff` manage nobody here, so they only
 * ever appear as possible TARGETS, never as the acting role.
 */
export const ROLE_RANK: Record<string, number> = {
  admin: 5,
  client: 4,
  'organization-manager': 3,
  organization: 2,
  staff: 1,
};

/** Only roles with a "manage other users" screen may impersonate. */
export const MIN_ACTOR_RANK = ROLE_RANK['organization-manager'];

export interface ImpersonationState {
  /** Target's user_id (sent as the X-Impersonate-Subject header value). */
  sub: string;
  /** What the app-bar pill shows. */
  displayName: string;
  /** The target's role — becomes the effective role app-wide. */
  role: string;
  /** The target's numeric role_id — becomes the effective role id app-wide. */
  roleId: number;
  /** The target's plan module ids — becomes the effective gating set. */
  planModules: number[];
}

export const getImpersonation = (): ImpersonationState | null => {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return null;
  }
};

export const startImpersonation = (state: ImpersonationState): void => {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export const stopImpersonation = (): void => {
  sessionStorage.removeItem(STORAGE_KEY);
};

const rankOf = (roleName: string | null | undefined): number =>
  ROLE_RANK[String(roleName ?? '').toLowerCase()] ?? 0;

/** Role name for a user record's numeric role_id, or null if unrecognized. */
export const roleNameOf = (roleId: number | null | undefined): string | null =>
  roleId != null ? ROLE_NAMES_BY_ID[roleId] ?? null : null;

/**
 * Mirror of the API's guard (strictly downward, enabled, never self, never
 * nested) so the row action only shows where the request would succeed.
 */
export const canImpersonate = (
  actorRole: string | null,
  actorUserId: number | null,
  target: { user_id: number; role_id: number; status?: number }
): boolean => {
  if (getImpersonation()) return false;
  if (actorUserId == null || rankOf(actorRole) < MIN_ACTOR_RANK) return false;

  const targetRole = roleNameOf(target.role_id);
  if (!targetRole || target.status !== 1) return false;
  if (target.user_id === actorUserId) return false;

  return rankOf(actorRole) > rankOf(targetRole);
};
